import {
  useAuthenticatedQuery,
  useAuthenticatedMutation,
  type ApiQueryOptions,
  type ApiMutationOptions,
} from '@/hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import documentsApi from '@/api/documentsApi';
import type { ApiResponse, DocumentModel } from '@/api/types';

type DocumentPayload = Record<string, unknown>;
type DocumentListResponse = ApiResponse<DocumentModel[]>;
type DocumentResponse = ApiResponse<DocumentModel>;

// Query keys for document-related queries
export const DOCUMENTS_KEYS = {
  all: ['documents'] as const,
  byId: (id: string) => ['documents', id] as const,
  byStatus: (status: string) => ['documents', 'status', status] as const,
};

/**
 * Hook for fetching all documents
 */
export function useDocuments(
  options: ApiQueryOptions<DocumentListResponse> = {},
) {
  return useAuthenticatedQuery<DocumentListResponse>(
    DOCUMENTS_KEYS.all,
    token => documentsApi.getDocuments(token),
    options,
  );
}

/**
 * Hook for fetching a specific document by ID
 */
export function useDocument(
  id: string,
  options: ApiQueryOptions<DocumentResponse> = {},
) {
  return useAuthenticatedQuery<DocumentResponse>(
    DOCUMENTS_KEYS.byId(id),
    token => documentsApi.getDocumentById(id, token),
    {
      enabled: !!id,
      ...options,
    },
  );
}

/**
 * Hook for creating a new document
 */
export function useCreateDocument(
  options: ApiMutationOptions<DocumentResponse, { data: DocumentPayload }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<DocumentResponse, { data: DocumentPayload }>(
    ({ data, token }) => documentsApi.createDocument(data, token),
    {
      onSuccess: () => {
        // Invalidate documents list to refetch
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for updating a document
 */
export function useUpdateDocument(
  options: ApiMutationOptions<
    DocumentResponse,
    { id: string; documentData: DocumentPayload }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { id: string; documentData: DocumentPayload }
  >(
    ({ id, documentData, token }) =>
      documentsApi.updateDocument(id, documentData, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate both the specific document and the list
        queryClient.invalidateQueries({
          queryKey: DOCUMENTS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for deleting a document
 */
export function useDeleteDocument(
  options: ApiMutationOptions<ApiResponse<null>, { id: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<ApiResponse<null>, { id: string }>(
    ({ id, token }) => documentsApi.deleteDocument(id, token),
    {
      onSuccess: (_data, variables) => {
        // Remove from cache and invalidate list
        queryClient.removeQueries({
          queryKey: DOCUMENTS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for uploading a document
 */
export function useUploadDocument(
  options: ApiMutationOptions<DocumentResponse, { formData: FormData }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<DocumentResponse, { formData: FormData }>(
    ({ formData, token }) => documentsApi.uploadDocument(formData, token),
    {
      onSuccess: () => {
        // Invalidate documents list to show the new upload
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for downloading a document
 */
export function useDownloadDocument(
  options: ApiMutationOptions<Blob, { id: string }> = {},
) {
  return useAuthenticatedMutation<Blob, { id: string }>(
    ({ id, token }) => documentsApi.downloadDocument(id, token),
    options,
  );
}

/**
 * Hook for updating document status
 */
export function useUpdateDocumentStatus(
  options: ApiMutationOptions<
    DocumentResponse,
    { id: string; status: unknown }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { id: string; status: unknown }
  >(
    ({ id, status, token }) =>
      documentsApi.updateDocumentStatus(id, status, token),
    {
      onSuccess: (_data, variables) => {
        // Invalidate both the specific document and the list
        queryClient.invalidateQueries({
          queryKey: DOCUMENTS_KEYS.byId(variables.id),
        });
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting document cancellation comment
 */
export function useSubmitDocumentCancellation(
  options: ApiMutationOptions<
    DocumentResponse,
    { documentId: string; comment: string }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { documentId: string; comment: string }
  >(
    ({ documentId, comment, token }) =>
      documentsApi.submitCancellationComment(documentId, comment, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: DOCUMENTS_KEYS.byId(variables.documentId),
        });
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for accepting document payment
 */
export function useAcceptDocument(
  options: ApiMutationOptions<DocumentResponse, { documentId: string }> = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<DocumentResponse, { documentId: string }>(
    ({ documentId, token }) => documentsApi.acceptDocument(documentId, token),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: DOCUMENTS_KEYS.byId(variables.documentId),
        });
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting document feedback
 */
export function useSubmitDocumentFeedback(
  options: ApiMutationOptions<
    DocumentResponse,
    { feedbackData: DocumentPayload & { document_id?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { feedbackData: DocumentPayload & { document_id?: string } }
  >(
    ({ feedbackData, token }) =>
      documentsApi.submitFeedback(feedbackData, token),
    {
      onSuccess: (_data, variables) => {
        if (variables.feedbackData.document_id) {
          queryClient.invalidateQueries({
            queryKey: DOCUMENTS_KEYS.byId(variables.feedbackData.document_id),
          });
        }
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for submitting facture (invoice) for a document
 */
export function useSubmitFacture(
  options: ApiMutationOptions<
    DocumentResponse,
    { factureData: DocumentPayload & { document_id?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { factureData: DocumentPayload & { document_id?: string } }
  >(
    ({ factureData, token }) => documentsApi.submitFacture(factureData, token),
    {
      onSuccess: (_data, variables) => {
        if (variables.factureData.document_id) {
          queryClient.invalidateQueries({
            queryKey: DOCUMENTS_KEYS.byId(variables.factureData.document_id),
          });
        }
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for updating document step/status
 */
export function useUpdateDocumentStep(
  options: ApiMutationOptions<
    DocumentResponse,
    { documentData: DocumentPayload & { documentId?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { documentData: DocumentPayload & { documentId?: string } }
  >(
    ({ documentData, token }) =>
      documentsApi.updateDocumentStep(documentData, token),
    {
      onSuccess: (_data, variables) => {
        if (variables.documentData.documentId) {
          queryClient.invalidateQueries({
            queryKey: DOCUMENTS_KEYS.byId(variables.documentData.documentId),
          });
        }
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}

/**
 * Hook for completing a document request
 */
export function useCompleteDocumentRequest(
  options: ApiMutationOptions<
    DocumentResponse,
    { completionData: DocumentPayload & { document_id?: string } }
  > = {},
) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutation<
    DocumentResponse,
    { completionData: DocumentPayload & { document_id?: string } }
  >(
    ({ completionData, token }) =>
      documentsApi.completeRequest(completionData, token),
    {
      onSuccess: (_data, variables) => {
        if (variables.completionData.document_id) {
          queryClient.invalidateQueries({
            queryKey: DOCUMENTS_KEYS.byId(variables.completionData.document_id),
          });
        }
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_KEYS.all });
      },
      ...options,
    },
  );
}
